import React, { useState, useMemo, useEffect } from "react";
import MonthDropdown from "./MonthDropdown";
import YearDropdown from "./YearDropdown";
import WorkingHourSelector from "./WorkingHourSelector";
import DisplayModeSelector from "./DisplayModeSelector";
import "../../App.css";
import {
  DisplayMode,
  IAvailableSlot,
  IReservedSlot,
  defaultWorkingHours,
  getDatesArray,
  getHourBounds,
  IInterviewDate,
} from "./helper";
import AvailabilityTable from "./AvailablilityTable";
import { axiosInstant, endpoints } from "../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ErrorType, ROUTES } from "../common";
import { Col, Row, Typography } from "antd";
import { showError, showSuccess } from "../../Notifications";
import { PendingSuggestions } from "./SlotSuggestions";
import { TimezoneNote } from "./TimezoneNote";
import { QuickFill } from "./QuickFill";
import { delocalizeSlots, loadLocalMonth } from "./slotTime";
import { Pattern, applyPattern, laterMonths } from "./slotPattern";

type CalendarProps = {
  userType: 'manager' | 'candidate';
  userId?: string;
  onSelectSlot?: (slot: IInterviewDate) => void;
};

const Calendar: React.FC<CalendarProps> = ({ userType, userId, onSelectSlot = () => {} }) => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedHours, setSelectedHours] = useState(defaultWorkingHours);
  const [displayMode, setDisplayMode] = useState(DisplayMode.WORKINGDAY);
  const [reservedSlots, setReservedSlots] = useState<Array<IReservedSlot>>([]);
  const [defaultSlots, setDefaultSlots] = useState<Array<IAvailableSlot>>([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  // The grid's current slots, including edits not saved yet - Quick fill builds on these.
  const draftSlots = React.useRef<Array<IAvailableSlot>>([]);
  // Quick fills waiting to be carried into the months after the one on screen.
  const pendingPatterns = React.useRef<Pattern[]>([]);

  const datesArray = useMemo(
    () => getDatesArray(selectedMonth, displayMode, selectedYear),
    [selectedMonth, displayMode, selectedYear]
  );

  const getCalendarEndpoint = (month: number, year: number) => {
    if (userType === 'manager') {
      return endpoints.getMyCalendar(month, year);
    }
    if (userType === 'candidate' && !userId) {
      // Candidate viewing their own calendar
      return endpoints.getMyCandidateCalendar(month, year);
    }
    // Recruiter viewing a candidate's calendar
    return endpoints.getCandidateCalendar(userId!, month, year);
  };

  const postCalendarEndpoint =
    userType === 'manager'
      ? endpoints.myCalendar
      : endpoints.myCandidateCalendar;

  const loadCalendar = async () => {
    console.log('Fetching calendar for month:', selectedMonth, 'year:', selectedYear, 'userType:', userType, 'userId:', userId);
    try {
      // The API speaks UTC; the grid is in the viewer's time zone.
      const { availableSlots, reservedSlots } = await loadLocalMonth(
        selectedMonth,
        selectedYear,
        async (utcMonth, utcYear) => {
          const { data } = await axiosInstant.get(getCalendarEndpoint(utcMonth - 1, utcYear));
          return data || {};
        }
      );
      setDefaultSlots(availableSlots);
      if (availableSlots.length) {
        setSelectedHours(getHourBounds(availableSlots));
      }
      setReservedSlots(reservedSlots);
    } catch (err) {
      const error = err as ErrorType;
      if (error.response.status === 422) setConnectionError(true);
      else navigate(ROUTES.LANDING);
    }
    setLoading(false);
  };

  const fetchUtcMonth = async (utcMonth: number, utcYear: number) => {
    const { data } = await axiosInstant.get(getCalendarEndpoint(utcMonth - 1, utcYear));
    return data || {};
  };

  const onSlotUpdate = async (slots: Array<IAvailableSlot>) => {
    try {
      // Back to UTC for storage, naming the span of UTC time this local month covers.
      await axiosInstant.post(postCalendarEndpoint, delocalizeSlots(slots, selectedMonth, selectedYear));

      // A quick fill can run past this month. Each later month is loaded, has the
      // same pattern applied and is saved on its own.
      const patterns = pendingPatterns.current;
      const months = new Map<string, { month0: number; year: number }>();
      patterns.forEach((p) => laterMonths(p, selectedMonth, selectedYear).forEach((m) => months.set(`${m.year}-${m.month0}`, m)));
      for (const { month0, year } of Array.from(months.values())) {
        const loaded = await loadLocalMonth(month0, year, fetchUtcMonth);
        let monthSlots = loaded.availableSlots;
        for (const p of patterns) {
          if (laterMonths(p, selectedMonth, selectedYear).some((m) => m.month0 === month0 && m.year === year)) {
            monthSlots = applyPattern(monthSlots, loaded.reservedSlots, p, month0, year).slots;
          }
        }
        await axiosInstant.post(postCalendarEndpoint, delocalizeSlots(monthSlots, month0, year));
      }
      pendingPatterns.current = [];
      showSuccess(months.size ? `Calendar updated, including ${months.size} later month${months.size === 1 ? "" : "s"}` : "Calendar updated successfully");
      triggerRefresh();
    } catch (err) {
      showError(err);
    }
  };

  const triggerRefresh = () => setRefreshFlag(f => !f);

  // Whose calendar this is to edit: a manager's own, or a candidate's own (not a
  // recruiter looking at one).
  const isOwnCalendar = userType === 'manager' || (userType === 'candidate' && !userId);

  useEffect(() => {
    // The grid reloads from the server, so fills waiting on the old one are dropped.
    pendingPatterns.current = [];
    loadCalendar();
  }, [selectedMonth, selectedYear, refreshFlag]);
 
  const centeredText = (text: string) => (
    <Row>
      <Col span={24}>
        <Typography.Title level={3} style={{ textAlign: "center" }}>
          {text}
        </Typography.Title>
      </Col>
    </Row>
  );

  if (!loading) {
    if (connectionError) {
      return centeredText(
        "You have not connected your calendar with chornoflow yet."
      );
    }

    return (
      <div className="calendar-container">
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <MonthDropdown {...{ selectedMonth, setSelectedMonth }} />
          <YearDropdown {...{ selectedYear, setSelectedYear }} />
        </div>
        {/* Own calendar only: requests from a recruiter to open specific slots. */}
        {(userType === 'manager' || !userId) && <PendingSuggestions onChanged={triggerRefresh} />}
        <TimezoneNote />
        {isOwnCalendar && (
          <QuickFill
            month0={selectedMonth}
            year={selectedYear}
            getCurrent={() => draftSlots.current}
            reservedSlots={reservedSlots}
            onApply={(slots, { from, until }, pattern) => {
              setDefaultSlots(slots);
              if (laterMonths(pattern, selectedMonth, selectedYear).length) pendingPatterns.current.push(pattern);
              // Rows only exist for the hours on screen, so widen them to show the fill.
              setSelectedHours((h) => ({ startTime: Math.min(h.startTime, from), endTime: Math.max(h.endTime, until) }));
            }}
          />
        )}
        <DisplayModeSelector {...{ displayMode, setDisplayMode }} />
        <WorkingHourSelector {...{ selectedHours, setSelectedHours }} />
        <AvailabilityTable
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          displayMode={displayMode}
          
          {...{
            datesArray,
            selectedHours,
            defaultSlots,
            reservedSlots,
            onUpdate: onSlotUpdate,
            onSlotsChange: (slots: Array<IAvailableSlot>) => { draftSlots.current = slots; },
            ...(userType === 'manager' || (userType === 'candidate' && !userId)
              ? {}
              : { onSelect: (date, hour) => onSelectSlot({ date, hour }) }
            ),
          }}
        />
      </div>
    );
  }
  return centeredText("Loading...");
};

export default Calendar;
